import type {
  PersistentStoragePermissionState,
  StorageDiagnostics,
} from "@/runtime/events";

function requireOpfs(): Promise<FileSystemDirectoryHandle> {
  if (!navigator.storage.getDirectory) {
    throw new Error("OPFS is unavailable in this browser");
  }

  return navigator.storage.getDirectory();
}

async function ensureDirectory(
  root: FileSystemDirectoryHandle,
  segments: string[],
): Promise<FileSystemDirectoryHandle> {
  let current = root;

  for (const segment of segments) {
    current = await current.getDirectoryHandle(segment, { create: true });
  }

  return current;
}

export async function getStorageDiagnostics(): Promise<StorageDiagnostics> {
  const estimate = navigator.storage.estimate
    ? await navigator.storage.estimate()
    : { quota: 0, usage: 0 };
  const persistedApiAvailable = typeof navigator.storage.persisted === "function";
  const persistApiAvailable = typeof navigator.storage.persist === "function";
  const persisted = persistedApiAvailable ? await navigator.storage.persisted() : false;
  const opfsAvailable = typeof navigator.storage.getDirectory === "function";
  const quota = estimate.quota ?? 0;
  const usage = estimate.usage ?? 0;
  const available = quota > usage ? quota - usage : 0;
  const permissionState = await getPersistentStoragePermissionState();

  return {
    quota,
    usage,
    available,
    persisted,
    persistenceGranted: persisted,
    opfsAvailable,
    persistApiAvailable,
    persistedApiAvailable,
    permissionState,
  };
}

export interface PersistentStorageRequestResult {
  supported: boolean;
  beforePersisted: boolean;
  afterPersisted: boolean;
  requested: boolean;
  granted: boolean;
  permissionStateBefore: PersistentStoragePermissionState;
  permissionStateAfter: PersistentStoragePermissionState;
  errorMessage?: string;
}

async function getPersistentStoragePermissionState(): Promise<PersistentStoragePermissionState> {
  if (!("permissions" in navigator) || !navigator.permissions?.query) {
    return "unsupported";
  }

  try {
    const status = await navigator.permissions.query({
      name: "persistent-storage" as PermissionName,
    });
    return status.state;
  } catch {
    return "unsupported";
  }
}

export async function requestPersistentStorage(): Promise<PersistentStorageRequestResult> {
  const supported = typeof navigator.storage.persist === "function";
  const persistedApiAvailable = typeof navigator.storage.persisted === "function";
  const permissionStateBefore = await getPersistentStoragePermissionState();
  const beforePersisted = persistedApiAvailable ? await navigator.storage.persisted() : false;

  if (!supported) {
    return {
      supported,
      beforePersisted,
      afterPersisted: beforePersisted,
      requested: false,
      granted: beforePersisted,
      permissionStateBefore,
      permissionStateAfter: permissionStateBefore,
    };
  }

  let errorMessage: string | undefined;

  try {
    if (!beforePersisted) {
      await navigator.storage.persist();
    }
  } catch (error) {
    errorMessage =
      error instanceof Error ? error.message : "Persistent storage request failed";
  }

  const afterPersisted = persistedApiAvailable ? await navigator.storage.persisted() : false;
  const permissionStateAfter = await getPersistentStoragePermissionState();

  return {
    supported,
    beforePersisted,
    afterPersisted,
    requested: !beforePersisted,
    granted: afterPersisted,
    permissionStateBefore,
    permissionStateAfter,
    errorMessage,
  };
}

export async function writeBlobToOpfs(path: string, blob: Blob): Promise<void> {
  const root = await requireOpfs();
  const segments = path.split("/").filter(Boolean);
  const fileName = segments.pop();

  if (!fileName) {
    throw new Error(`Invalid OPFS path: ${path}`);
  }

  const directory = await ensureDirectory(root, segments);
  const fileHandle = await directory.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
}

export async function readOpfsFile(path: string): Promise<File> {
  const root = await requireOpfs();
  const segments = path.split("/").filter(Boolean);
  const fileName = segments.pop();

  if (!fileName) {
    throw new Error(`Invalid OPFS path: ${path}`);
  }

  const directory = await ensureDirectory(root, segments);
  const fileHandle = await directory.getFileHandle(fileName);
  return fileHandle.getFile();
}

export async function readOpfsBytes(path: string): Promise<Uint8Array> {
  return new Uint8Array(await (await readOpfsFile(path)).arrayBuffer());
}

export async function readOpfsText(path: string): Promise<string> {
  return (await readOpfsFile(path)).text();
}

export async function openOpfsWritable(
  path: string,
  options: FileSystemCreateWritableOptions = {},
): Promise<FileSystemWritableFileStream> {
  const root = await requireOpfs();
  const segments = path.split("/").filter(Boolean);
  const fileName = segments.pop();

  if (!fileName) {
    throw new Error(`Invalid OPFS path: ${path}`);
  }

  const directory = await ensureDirectory(root, segments);
  const fileHandle = await directory.getFileHandle(fileName, { create: true });
  return fileHandle.createWritable(options);
}

export function syncAccessHandleSupported(): boolean {
  return (
    typeof FileSystemFileHandle !== "undefined" &&
    typeof (FileSystemFileHandle.prototype as unknown as {
      createSyncAccessHandle?: () => unknown;
    }).createSyncAccessHandle === "function"
  );
}

export async function openOpfsSyncAccessHandle(
  path: string,
): Promise<FileSystemSyncAccessHandle> {
  const root = await requireOpfs();
  const segments = path.split("/").filter(Boolean);
  const fileName = segments.pop();

  if (!fileName) {
    throw new Error(`Invalid OPFS path: ${path}`);
  }

  const directory = await ensureDirectory(root, segments);
  const fileHandle = await directory.getFileHandle(fileName, { create: true });
  return fileHandle.createSyncAccessHandle();
}

export interface OpfsRootSnapshot {
  fileCount: number;
  dirCount: number;
  totalBytes: number;
  durationMs: number;
  top: Array<{ path: string; bytes: number }>;
  error: string | null;
}

/**
 * Walk the OPFS root recursively and return a ground-truth snapshot of what
 * the origin has on disk: file count, directory count, total bytes, and the
 * largest few files. Used as a cross-check against `navigator.storage.estimate()`
 * which on iOS Safari has been observed to report `usage` at 2–8× actual
 * bytes. When we see a divergence between this snapshot and `estimate().usage`,
 * we have direct evidence that the UA's accounting is not reflecting disk
 * reality.
 *
 * Cost: one `getFile()` per file, which on iOS is roughly an fstat. At
 * hundreds of files in a populated origin, this can take tens to a few
 * hundred ms. Callers should use sparingly and record `durationMs` so we can
 * observe the probe's own cost.
 */
export async function walkOpfsRoot(limitTopFiles = 8): Promise<OpfsRootSnapshot> {
  const started = performance.now();
  let fileCount = 0;
  let dirCount = 0;
  let totalBytes = 0;
  const largest: Array<{ path: string; bytes: number }> = [];

  async function walk(dir: FileSystemDirectoryHandle, prefix: string): Promise<void> {
    const iterable = dir as unknown as {
      entries: () => AsyncIterable<[string, FileSystemHandle]>;
    };
    for await (const [name, handle] of iterable.entries()) {
      const here = prefix ? `${prefix}/${name}` : name;
      if (handle.kind === "file") {
        fileCount += 1;
        try {
          const file = await (handle as FileSystemFileHandle).getFile();
          totalBytes += file.size;
          if (largest.length < limitTopFiles) {
            largest.push({ path: here, bytes: file.size });
            largest.sort((a, b) => b.bytes - a.bytes);
          } else if (file.size > largest[largest.length - 1].bytes) {
            largest[largest.length - 1] = { path: here, bytes: file.size };
            largest.sort((a, b) => b.bytes - a.bytes);
          }
        } catch {
          // Count the file but skip size.
        }
      } else if (handle.kind === "directory") {
        dirCount += 1;
        await walk(handle as FileSystemDirectoryHandle, here);
      }
    }
  }

  try {
    const root = await requireOpfs();
    await walk(root, "");
    return {
      fileCount,
      dirCount,
      totalBytes,
      durationMs: Math.round(performance.now() - started),
      top: largest,
      error: null,
    };
  } catch (err) {
    return {
      fileCount,
      dirCount,
      totalBytes,
      durationMs: Math.round(performance.now() - started),
      top: largest,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/** Sync-access-handle size readback: opens the file separately (cheap) so the
 *  caller can compare actual on-disk bytes vs the bytes they think they wrote.
 *  Returns null on any error so this can never break the install path. */
export async function readOpfsFileSize(path: string): Promise<number | null> {
  try {
    const file = await readOpfsFile(path);
    return file.size;
  } catch {
    return null;
  }
}

export async function removeOpfsFile(path: string): Promise<void> {
  const root = await requireOpfs();
  const segments = path.split("/").filter(Boolean);
  const fileName = segments.pop();

  if (!fileName) {
    throw new Error(`Invalid OPFS path: ${path}`);
  }

  const directory = await ensureDirectory(root, segments);
  await directory.removeEntry(fileName);
}
